import type { Aggregator } from '../domain/types'

interface ConfirmJourneyCore {
  confirm: (aggregatorId: string) => Promise<Aggregator>
  apply: (journey: Aggregator) => void
  loading: (aggregatorId: string, value: boolean) => void
  success: () => void
  error: (error: unknown) => void
}

/** Sem patch otimista (rollback opcional). */
export interface PlainConfirmJourneyDeps extends ConfirmJourneyCore {
  optimistic?: undefined
  rollback?: () => void
}

/** Type-level: se houve patch otimista ANTES do POST, rollback no erro é obrigatório. */
export interface OptimisticConfirmJourneyDeps extends ConfirmJourneyCore {
  optimistic: (aggregatorId: string) => void
  rollback: () => void
}

export type ConfirmJourneyDeps = PlainConfirmJourneyDeps | OptimisticConfirmJourneyDeps

/** Coordena a confirmação e barra reentradas síncronas do mesmo botão.
 *  `confirm` já devolve a jornada atualizada (POST autoritativo) — `apply` a aplica
 *  direto no estado local; não há reload de lista aqui (seria redundante e só atrasaria `success`). */
export function createJourneyConfirmer(deps: ConfirmJourneyDeps) {
  const inFlight = new Set<string>()

  return async (aggregatorId: string): Promise<void> => {
    if (inFlight.has(aggregatorId)) {return}
    inFlight.add(aggregatorId)
    deps.loading(aggregatorId, true)
    try {
      deps.optimistic?.(aggregatorId)
      const updated = await deps.confirm(aggregatorId)
      deps.apply(updated)
      deps.success()
    } catch (error) {
      deps.rollback?.()
      deps.error(error)
    } finally {
      inFlight.delete(aggregatorId)
      deps.loading(aggregatorId, false)
    }
  }
}
